/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import moment from 'moment';

// Utils
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';

// Components
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';

// APIs
import {
  getPeriodosValidacion,
  getPeriodoValidacion,
  postPeriodoValidacion,
  patchPeriodoValidacion,
  deletePeriodoValidacion,
  autorizarPeriodoValidacion,
} from 'api/catalogos/PeriodosValidacion';

const SolicitudesPeriodosValidacion = ({
  selected, // ID de la Solicitud Padre
  permission,
  estatusInactivo,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      if (selected) {
        const _data = await getPeriodosValidacion({ solicitud: selected });
        setData(formatReceived(_data));
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [selected]);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    try {
      const _res = await getPeriodoValidacion(key);
      if (_res) {
        form.setFieldsValue(formatReceived(_res));
        setVisible(true);
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const [key] = selectedRowKeys;
      const payload = {
        ...values,
        solicitud: selected,
        fecha_inicial: values.fecha_inicial.format('YYYY-MM-DD'),
        fecha_final: values.fecha_final.format('YYYY-MM-DD'),
      };

      // eslint-disable-next-line max-len
      const saved = await (key ? patchPeriodoValidacion(key, payload) : postPeriodoValidacion(payload));
      if (saved) {
        onCancel();
        await fetchData();
      }
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'No. de Periodo',
      dataIndex: 'no_de_periodo',
      key: 'no_de_periodo',
      width: 200,
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : '-'),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : '-'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus_descripcion',
      key: 'estatus',
      width: 150,
    },
  ];

  return (
    <Row style={{ marginTop: '20px' }}>
      <Col span={24}>
        <p style={{ color: '#D88F4F', fontWeight: 600 }}>Periodo de Validación</p>
        <Table
          cols={columns}
          data={data}
          rowSelection={{ selectedRowKeys, type: 'radio', onChange: (keys) => setSelectedRowKeys(keys) }}
          handleOnRowClick={(record) => {
            setSelectedRowKeys([record.id]);
            setSelectedData(record);
          }}
          controls={{
            onClickAdd: () => { form.resetFields(); setVisible(true); },
            onClickEdit,
            onClickDelete: () => setVisibleAlert(true),
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'periodovalidacion', action: 'autorizar' },
              text: 'Autorizar',
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableAdd={estatusInactivo}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
        />
      </Col>

      <Modal
        open={open}
        footer={null}
        width="600px"
        title={(
          <FormSubmitControls
            label="Periodo de Validación"
            onFinish={onFinish}
            onCancel={onCancel}
          />
)}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="no_de_periodo"
            label="No. de Periodo"
            rules={[{ required: true }]}
            extra="Campo alfanumérico de captura libre"
          >
            <Input placeholder="Ej. 1 - 2025" />
          </Form.Item>

          <Row gutter={10}>
            <Col span={12}>
              <Form.Item
                name="fecha_inicial"
                label="Fecha Inicial"
                rules={[{ required: true }]}
                extra="Captura manual"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="fecha_final"
                label="Fecha Final"
                rules={[{ required: true }]}
                extra="Captura manual"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <ModalDelete
        open={visibleAlert}
        onDelete={async () => {
          await deletePeriodoValidacion(selectedRowKeys[0]);
          setVisibleAlert(false);
          fetchData();
        }}
        onCancel={() => setVisibleAlert(false)}
      />

      <ModalConfirmation
        open={visibleAutorizar}
        ConfirmAutorize
        onFinish={async () => {
          await autorizarPeriodoValidacion(selectedRowKeys[0]);
          setVisibleAutorizar(false);
          fetchData();
        }}
        onCancel={() => setVisibleAutorizar(false)}
      />
    </Row>
  );
};

export default SolicitudesPeriodosValidacion;
